import type { LoginFlow, SettingsFlow, UiNodeInputAttributes } from '@ory/client';

/**
 * Pure WebAuthn ceremony + Kratos-settings-flow helpers for passkey enrollment.
 * Extracted from PasskeySettings.tsx so the encoding (the spec-critical part)
 * and the flow-node parsing are unit-testable without a DOM/React.
 *
 * The base64url encoding mirrors Kratos' webauthn.js __oryWebAuthnBuffer{Decode,
 * Encode} exactly, and the registration response shape matches what the Kratos
 * passkey settings strategy parses (id, rawId, type, response.{attestationObject,
 * clientDataJSON}).
 */

// base64url -> ArrayBuffer. Returns a concrete ArrayBuffer (not a typed-array
// view) so values satisfy the WebAuthn `BufferSource` types without a
// SharedArrayBuffer union.
export function bufferDecode(value: string): ArrayBuffer {
  const bin = atob(value.replaceAll('-', '+').replaceAll('_', '/'));
  const buf = new ArrayBuffer(bin.length);
  const view = new Uint8Array(buf);
  for (let i = 0; i < bin.length; i++) view[i] = bin.charCodeAt(i);
  return buf;
}

// ArrayBuffer -> unpadded base64url.
export function bufferEncode(value: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(value))).replaceAll('+', '-').replaceAll('/', '_').replaceAll('=', '');
}

// The `passkey_create_data` field holds CredentialCreationOptions with buffers
// serialized as base64url strings — decoded before navigator.credentials.create.
interface PasskeyCreateData {
  publicKey: {
    rp: PublicKeyCredentialRpEntity;
    user: {
      name: string;
      displayName: string;
      id: string;
    };
    challenge: string;
    pubKeyCredParams: PublicKeyCredentialParameters[];
    timeout?: number;
    attestation?: AttestationConveyancePreference;
    authenticatorSelection?: AuthenticatorSelectionCriteria;
    extensions?: AuthenticationExtensionsClientInputs;
    excludeCredentials?: {
      type: PublicKeyCredentialType;
      id: string;
      transports?: AuthenticatorTransport[];
    }[];
  };
}

/** Decode the serialized `passkey_create_data` into real CredentialCreationOptions. */
export function buildCreationOptions(raw: string): PublicKeyCredentialCreationOptions {
  const pk = (JSON.parse(raw) as PasskeyCreateData).publicKey;
  return {
    rp: pk.rp,
    user: {
      name: pk.user.name,
      displayName: pk.user.displayName,
      id: bufferDecode(pk.user.id)
    },
    challenge: bufferDecode(pk.challenge),
    pubKeyCredParams: pk.pubKeyCredParams,
    timeout: pk.timeout,
    attestation: pk.attestation,
    authenticatorSelection: pk.authenticatorSelection,
    extensions: pk.extensions,
    excludeCredentials: pk.excludeCredentials?.map(c => ({
      type: c.type,
      id: bufferDecode(c.id),
      transports: c.transports
    }))
  };
}

/** Encode the registration result into the JSON Kratos' passkey strategy parses. */
export function encodeRegistration(credential: PublicKeyCredential): string {
  const attestation = credential.response as AuthenticatorAttestationResponse;
  return JSON.stringify({
    id: credential.id,
    rawId: bufferEncode(credential.rawId),
    type: credential.type,
    response: {
      attestationObject: bufferEncode(attestation.attestationObject),
      clientDataJSON: bufferEncode(attestation.clientDataJSON)
    }
  });
}

// The login flow's `passkey_challenge` field holds CredentialRequestOptions
// with buffers serialized as base64url strings.
interface PasskeyRequestData {
  publicKey: {
    challenge: string;
    timeout?: number;
    rpId?: string;
    userVerification?: UserVerificationRequirement;
    allowCredentials?: {
      type: PublicKeyCredentialType;
      id: string;
      transports?: AuthenticatorTransport[];
    }[];
  };
}

/** Decode the serialized login `passkey_challenge` into real CredentialRequestOptions. */
export function buildRequestOptions(raw: string): PublicKeyCredentialRequestOptions {
  const pk = (JSON.parse(raw) as PasskeyRequestData).publicKey;
  return {
    challenge: bufferDecode(pk.challenge),
    timeout: pk.timeout,
    rpId: pk.rpId,
    userVerification: pk.userVerification,
    allowCredentials: pk.allowCredentials?.map(c => ({
      type: c.type,
      id: bufferDecode(c.id),
      transports: c.transports
    }))
  };
}

/** Encode a login assertion into the JSON Kratos' passkey strategy parses. */
export function encodeAssertion(credential: PublicKeyCredential): string {
  const assertion = credential.response as AuthenticatorAssertionResponse;
  return JSON.stringify({
    id: credential.id,
    rawId: bufferEncode(credential.rawId),
    type: credential.type,
    response: {
      authenticatorData: bufferEncode(assertion.authenticatorData),
      clientDataJSON: bufferEncode(assertion.clientDataJSON),
      signature: bufferEncode(assertion.signature),
      userHandle: assertion.userHandle ? bufferEncode(assertion.userHandle) : null
    }
  });
}
export function inputValue(flow: SettingsFlow, name: string): string | undefined {
  const attrs = (flow.ui?.nodes ?? []).find(n => (n.attributes as UiNodeInputAttributes).name === name)?.attributes as UiNodeInputAttributes | undefined;
  return attrs?.value as string | undefined;
}

/**
 * Enrolled passkeys are surfaced as remove submit nodes — one per credential.
 * Kratos exposes two WebAuthn-backed methods and we manage both here:
 *   - `passkey`  (passwordless first factor)  -> `passkey_remove` nodes
 *   - `webauthn` (second factor; what an AAL2 step-up requires) -> `webauthn_remove`
 */
export interface EnrolledPasskey {
  credentialId: string;
  label: string;
  /** ISO timestamp from Kratos' label context, when present. */
  addedAt?: string;
  method: 'passkey' | 'webauthn';
}
export function enrolledPasskeys(flow: SettingsFlow): EnrolledPasskey[] {
  return (flow.ui?.nodes ?? []).filter(n => {
    const name = (n.attributes as UiNodeInputAttributes).name;
    return n.group === 'passkey' && name === 'passkey_remove' || n.group === 'webauthn' && name === 'webauthn_remove';
  }).map(n => {
    const attrs = n.attributes as UiNodeInputAttributes;
    // Kratos' label.text is the full "Remove passkey \"X\"" string; prefer
    // the display_name from label.context so the row reads just the name.
    const ctx = n.meta?.label?.context as {
      display_name?: string;
      added_at?: string;
    } | undefined;
    const label = ctx?.display_name ?? n.meta?.label?.text ?? 'Passkey';
    return {
      credentialId: String(attrs.value ?? ''),
      label,
      addedAt: ctx?.added_at,
      method: (n.group === 'webauthn' ? 'webauthn' : 'passkey') as EnrolledPasskey['method']
    };
  });
}

/**
 * Creation options for the second-factor `webauthn` method. Kratos serves them
 * on the `webauthn_register_trigger` node: newer releases put the JSON in the
 * node value; older ones only embed it in the inline
 * `window.__oryWebAuthnRegistration({...})` onclick handler (which we never
 * execute — we parse its argument instead, for the same no-eval/no-navigation
 * reasons documented above for the passkey method).
 */
/**
 * Request options for the second-factor `webauthn` method on a LOGIN flow.
 * Kratos serves them on the `webauthn_login_trigger` node
 * (subtrees/kratos/x/webauthnx/nodes.go:49-57): the JSON lives in the node
 * value, and also inside the inline `window.__oryWebAuthnLogin({...})` onclick
 * handler on older releases. We never execute the handler — we parse its
 * argument, for the same no-eval/no-navigation reasons documented below for
 * webauthnRegisterOptions.
 */
export function webauthnLoginOptions(flow: LoginFlow): PublicKeyCredentialRequestOptions | null {
  const node = (flow.ui?.nodes ?? []).find(n => n.group === 'webauthn' && (n.attributes as UiNodeInputAttributes).name === 'webauthn_login_trigger');
  if (!node) return null;
  const attrs = node.attributes as UiNodeInputAttributes & {
    onclick?: string;
  };
  let raw: string | undefined;
  const v = attrs.value;
  if (typeof v === 'string' && v.trim().startsWith('{')) raw = v;
  if (!raw && typeof attrs.onclick === 'string') {
    const m = attrs.onclick.match(/__oryWebAuthnLogin\((.*)\)\s*;?\s*$/s);
    if (m) raw = m[1];
  }
  if (!raw) return null;
  try {
    return buildRequestOptions(raw);
  } catch {
    // Malformed options must read as "unavailable", not crash the page.
    return null;
  }
}
export function webauthnRegisterOptions(flow: SettingsFlow): PublicKeyCredentialCreationOptions | null {
  const node = (flow.ui?.nodes ?? []).find(n => n.group === 'webauthn' && (n.attributes as UiNodeInputAttributes).name === 'webauthn_register_trigger');
  if (!node) return null;
  const attrs = node.attributes as UiNodeInputAttributes & {
    onclick?: string;
  };
  let raw: string | undefined;
  const v = attrs.value;
  if (typeof v === 'string' && v.trim().startsWith('{')) raw = v;
  if (!raw && typeof attrs.onclick === 'string') {
    const m = attrs.onclick.match(/__oryWebAuthnRegistration\((.*)\)\s*;?\s*$/s);
    if (m) raw = m[1];
  }
  if (!raw) return null;
  try {
    return buildCreationOptions(raw);
  } catch {
    // Malformed options must read as "unavailable", not crash the section.
    return null;
  }
}